/**
 * AwardBadges Component
 * Displays award/reaction badges on thread cards and detail views
 * Per backend engineer's AWARDS-FRONTEND-GUIDE.md
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AWARD_CONFIG, formatAwardCount } from "./AwardConfig";
import "./AwardBadges.css";

// Animation variants
const badgeVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 500, damping: 25 },
  },
  exit: { scale: 0, opacity: 0, transition: { duration: 0.15 } },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

/**
 * AwardBadges - Display award badges with counts
 * @param {Object} props
 * @param {Object} props.reactionCounts - Object with count for each award type
 * @param {number} props.totalReactions - Total count of all awards
 * @param {string} props.size - Size variant: 'sm' | 'md' | 'lg'
 * @param {boolean} props.showEmpty - Whether to show component when no awards
 * @param {number} props.maxDisplay - Maximum number of awards to display
 * @param {Function} props.onClick - Click handler for badge
 */
const AwardBadges = ({
  reactionCounts,
  totalReactions,
  size = "md",
  showEmpty = false,
  maxDisplay = 5,
  onClick,
}) => {
  // Return null if no awards and showEmpty is false
  if ((!reactionCounts || totalReactions === 0) && !showEmpty) {
    return null;
  }

  // Get awards sorted by count (descending)
  const sortedAwards = Object.entries(reactionCounts || {})
    .filter(([_, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxDisplay);

  if (sortedAwards.length === 0 && !showEmpty) {
    return null;
  }

  return (
    <motion.div
      className={`award-badges award-badges-${size}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <AnimatePresence>
        {sortedAwards.map(([type, count]) => {
          const config = AWARD_CONFIG[type];
          if (!config) return null;

          return (
            <motion.span
              key={type}
              className="award-badge"
              data-type={type}
              variants={badgeVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              whileHover={{ scale: 1.08, y: -2 }}
              whileTap={{ scale: 0.95 }}
              style={{ background: config.gradient }}
              title={`${count} ${config.label} award${count !== 1 ? "s" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onClick?.(type);
              }}
            >
              <span className="award-badge-emoji">{config.emoji}</span>
              <span className="award-badge-count">{formatAwardCount(count)}</span>
            </motion.span>
          );
        })}
      </AnimatePresence>

      {/* Show overflow indicator if more awards than maxDisplay */}
      {Object.entries(reactionCounts || {}).filter(([_, count]) => count > 0).length > maxDisplay && (
        <span className="award-badge-overflow" title={`${totalReactions} total awards`}>
          +{totalReactions - sortedAwards.reduce((sum, [_, count]) => sum + count, 0)}
        </span>
      )}
    </motion.div>
  );
};

export default React.memo(AwardBadges);
